import { Product_2022_06 } from "@rechargeapps/storefront-client";
import useSWR from "swr";
import { useMemo, useState } from "react";;
import cn from "clsx";
import { Toggle } from "./Toggle";
import { tagItems } from "./ProductRecommendations";
import { Skeleton } from "./Skeleton";
import { useGlobal } from "../context/globalProvider";
import { handlePriorityList } from "./ProductRecommendations";
import { Input } from "./Input";
import ProductItem from "./SearchProductsItem";

const SearchProducts = ({
  onSubmit,
  swapFromProductId,
  swapFromVariantId,
  defaultTagNames,
  source,
  defaultProducts,
  collectionSource = "recharge_upsell_collection",
  subscriptionOnly = false,
  sale,
  properties,
}: {
  onSubmit: (product: Product_2022_06) => Promise<void>
  swapFromProductId?: string
  swapFromVariantId?: string
  defaultTagNames?: string[]
  /**
   * The source to send for events.
   */
  source?: string
  defaultProducts?: any[]
  /**
   * The source of the collection to fetch recommendations from.
   * This is the key used in recharge recommendations json.
   */
  collectionSource?: string
  subscriptionOnly?: boolean
  sale?: boolean
  properties?: any[],
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { customer, sendEvent } = useGlobal();
  const activeSubscriptions = customer?.include?.subscriptions?.filter((subscription) => subscription.status === "active");

  let productHandle = null;
  if (source !== "recharge_swap_collection") {
    const handles = activeSubscriptions?.map(({ product }) => product.handle);
    productHandle = handlePriorityList.find((handle) => handles?.includes(handle)) || handles?.[0];
  } else {
    const chosenProduct = activeSubscriptions?.find((subscription) => subscription.external_product_id.ecommerce === swapFromProductId);
    productHandle = chosenProduct?.product?.handle;
  }

  const [selectedProduct, setSelectedProduct] = useState<Product_2022_06 | null>(null);
  const [tags, setTags] = useState<{name: string, tagName: string[], selected: boolean}[]>(
    [...tagItems?.map((tag) => {
      return { ...tag, selected: defaultTagNames?.includes(tag.name) || false };
    }), false ? { 
      name: 'Shop All Sale', 
      tagName: ['ldw_sub_sale'], 
      selected: defaultTagNames?.includes("Shop All Sale") || false 
    } : null].filter(Boolean) as {name: string, tagName: string[], selected: boolean}[]
  );

  const selectedTags = tags.filter((tag) => tag.selected);

  // let key = `${window.Shopify.routes.root}products/${productHandle}?sections=recommendations-json`;
  let key = `${window.Shopify.routes.root}collections/shop-all?sections=recommendations-json`;
  const { data, isLoading, error } = useSWR([key, collectionSource], async ([url, collectionSource]) => {
    if (defaultProducts) {
      return {
        ...defaultProducts,
        products: defaultProducts.filter((product: any) => !product.tags?.includes('no-recharge')),
      };
    }

    const DEFAULT_RECOMMENDATIONS = {
      cross_sell_header: "",
      cross_sell_products: [],
      recharge_upsell_collection: [],
    };

    let shopifyRecommendations: {
      cross_sell_header: string;
      cross_sell_products: {
        id: string;
        title: string;
        image: string;
      }[];
    } = DEFAULT_RECOMMENDATIONS;

    const res = await fetch(url);
    const data = await res.json();
    const htmlString = data["recommendations-json"];
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    
    const sectionContent = tempDiv.querySelector('#shopify-section-recommendations-json')?.innerHTML;
    if (sectionContent) {
      const cleanedContent = sectionContent.trim().replace(/[\u0000-\u001F]/g, '');
      try {
        shopifyRecommendations = JSON.parse(cleanedContent);
      } catch (parseError) {
        console.error("portal: JSON parse error:", parseError);
        shopifyRecommendations = DEFAULT_RECOMMENDATIONS;
      }
    }

    return {
      ...shopifyRecommendations,
      products: (shopifyRecommendations[collectionSource] || [])
        .filter(Boolean)
        .filter((product: any) => (!product.tags?.includes('no-recharge') && product.type !== "Bundle")
      ),
    };
  }, {
    keepPreviousData: true,
  });

  const filteredProducts = useMemo(() => {
    return data?.products?.filter((product: any) => {
      // First filter by search query
      if (searchQuery) {
        const productTitle = product.title.toLowerCase();
        const query = searchQuery.toLowerCase();
        if (!productTitle.includes(query)) return false;
      }

      // Then filter by tags
      const selectedTags = tags.filter((tag) => tag.selected);
      if (selectedTags.length === 0) return true;
      const productTags = product.tags?.map((tag: string) => tag.toLowerCase());
      const selectedTagNames = selectedTags.flatMap((tag) => tag.tagName);
      const uniqueSelectedTagNames = [...new Set(selectedTagNames)];
      return uniqueSelectedTagNames.every((selectedTagName) => 
        productTags?.some((tag) => selectedTagName.includes(tag.toLowerCase()))
      );
    });
  }, [data?.products, tags, searchQuery]);

  const products = filteredProducts;

  return (
    <div>
      <div className="flex flex-col gap-2 mb-4">
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search for products..."
          autoFocus
        />
      </div>

      <div className="">
        <h3 className="text-sm font-medium uppercase tracking-wider mb-2">Filters:</h3>
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Toggle
              key={tag.name}
              size="sm"
              variant="outline"
              pressed={tag.selected}
              onClick={() => {
                setTags(tags.map((t) => {
                  if (tag.name === 'Shop All Stages') {
                    return { ...t, selected: t.name === 'Shop All Stages' ? !t.selected : false };
                  }

                  if (t.name === 'Shop All Stages') {
                    return { ...t, selected: false };
                  }

                  return { ...t, selected: t.name === tag.name ? !t.selected : t.selected };
                }));

                sendEvent('product_search_tag_clicked', {
                  tag_name: tag.name,
                  selected: !tag.selected
                });
              }}
            >
              {tag.name}
            </Toggle>
          ))}
        </div>
      </div>

      {
        isLoading && !selectedProduct && !error && !products && (
          <ul className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-x-2 md:gap-x-4 gap-y-10">
            {
              Array.from({ length: 10 }).map((_, index) => {
                return (
                  <li key={index}>
                    <div className="flex flex-col space-y-3">
                      <Skeleton className="w-full aspect-square rounded-xl" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                    </div>
                  </li>
                )
              })
            }
          </ul>
        )
      }

      {
        !selectedProduct && !error && products && products?.length > 0 && (
          <ul className={cn("mt-8 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-10", {
            "opacity-30 pointer-events-none": isLoading
          })}>
            {products?.map((product) => {
              return product.variants?.map((variant: any) => {
                const isShopAllPowders = selectedTags?.length === 1 && selectedTags?.[0]?.name === "Shop All Powders";
                const isHydrationVariant = variant.id === '46420251803884';
                const isHiddenVariant = variant.hidden_variant;

                const isSubscriptionSale = product.tags?.includes('ldw_sub_sale');
                if (!isSubscriptionSale && sale) {
                  variant.subscription_price = variant.subscription_compare_at_price;
                }

                // swaps only work for subscriptions, not one-time orders
                if (isShopAllPowders && isHydrationVariant || (!variant.selling_plan_id && swapFromProductId) || isHiddenVariant) {
                  return null;
                }

                const isRoutineBuilder = variant.is_routine_builder;

                return (
                  <li key={variant.id}>
                    <ProductItem 
                      source={source}
                      sale={isRoutineBuilder}
                      product={{
                        ...product,
                        selectedVariant: variant,
                        title: variant.title === "Default Title" ? product.title : `${product.title} - ${variant.title}`,
                      }} 
                      isCurrentProduct={swapFromVariantId === variant.id} 
                      onSubmit={onSubmit} 
                      properties={properties}
                      setSelectedProduct={setSelectedProduct} 
                      swapFromProductId={swapFromProductId} 
                      swapFromVariantId={swapFromVariantId}
                      subscriptionOnly={selectedTags?.length === 1 && selectedTags?.[0]?.name === "Shop All Powders" ? true : subscriptionOnly} 
                    />
                  </li>
                )
              })
            })}
          </ul>
        )
      }

      {
        !selectedProduct && !error && products && products?.length === 0 && (
          <div className="mt-8 text-sm">
            No products found matching your filters. Try changing your filters or search query.
          </div>
        )
      }
    </div>
  )
}

export default SearchProducts;